import { ExerciseStateMachine } from './ExerciseStateMachine';

export const LateralCoreFlexionState = {
  NEUTRAL: 'NEUTRAL',     
  RIGHT: 'RIGHT',         
  LEFT: 'LEFT',           
};

const NEUTRAL_THRESHOLD = 2;       
const FLEXION_THRESHOLD = 4;       
const MIN_FLEXION_ANGLE = 5;       
const MAX_FLEXION_ANGLE = 30;      
const MIN_TIME_IN_POSITION = 100;  
const MIN_TIME_BETWEEN_REPS = 600; 
const INTERNAL_COOLDOWN = 800;    

// Add debug logging flag
const DEBUG_LOGGING = false;

export class LateralCoreFlexionFSM extends ExerciseStateMachine {
  constructor() {
    const config = {
      initialState: LateralCoreFlexionState.NEUTRAL,
      initialStateData: {
        currentFlexionAngle: 0,
        previousFlexionAngle: 0,
        flexionHistory: [],
        formScore: 80,
        rangeOfMotionScore: 100,
        controlScore: 100,
        repFormScore: 0,
        exerciseProgress: 0,
        
        lastStateChangeTime: 0,
        lastRepTime: 0,
        maxRightFlexionAngle: 0,
        maxLeftFlexionAngle: 0,
        lastSideCompleted: null,
        visitedStates: ['NEUTRAL'],
        repCounted: false,          
        neutralReturnTime: 0,        
        stableInNeutral: true,       
        inInternalCooldown: false,   
        cooldownEndTime: 0,          
        areBothHandsRaised: false    
      },
      initialFormScores: {
        overall: 100,
        rangeOfMotion: 100,
        control: 100
      },
      transitions: {
        [LateralCoreFlexionState.NEUTRAL]: [
          {
            targetState: LateralCoreFlexionState.RIGHT,
            condition: (metrics, stateData, timestamp) => 
              metrics.currentFlexionAngle > FLEXION_THRESHOLD &&
              timestamp - stateData.lastStateChangeTime > MIN_TIME_IN_POSITION &&
              !stateData.inInternalCooldown,
            action: (fsm, metrics, timestamp) => {
              fsm.stateData.lastStateChangeTime = timestamp;
              fsm.stateData.visitedStates.push('RIGHT');
              fsm.stateData.stableInNeutral = false;
              fsm.stateData.repCounted = false;
              
              fsm.stateData.maxRightFlexionAngle = metrics.currentFlexionAngle;
            }
          },
          {
            targetState: LateralCoreFlexionState.LEFT,
            condition: (metrics, stateData, timestamp) => 
              metrics.currentFlexionAngle < -FLEXION_THRESHOLD &&
              timestamp - stateData.lastStateChangeTime > MIN_TIME_IN_POSITION &&
              !stateData.inInternalCooldown,
            action: (fsm, metrics, timestamp) => {
              fsm.stateData.lastStateChangeTime = timestamp;
              fsm.stateData.visitedStates.push('LEFT');
              fsm.stateData.stableInNeutral = false;
              fsm.stateData.repCounted = false;
              
              fsm.stateData.maxLeftFlexionAngle = metrics.currentFlexionAngle;
            }
          }
        ],
        
        [LateralCoreFlexionState.RIGHT]: [
          {
            targetState: LateralCoreFlexionState.NEUTRAL,
            condition: (metrics, stateData, timestamp) => 
              Math.abs(metrics.currentFlexionAngle) < NEUTRAL_THRESHOLD &&
              timestamp - stateData.lastStateChangeTime > MIN_TIME_IN_POSITION,
            action: (fsm, metrics, timestamp) => {
              fsm.stateData.lastStateChangeTime = timestamp;
              fsm.stateData.neutralReturnTime = timestamp;
              fsm.stateData.stableInNeutral = false;
              fsm.stateData.visitedStates.push('NEUTRAL');
            }
          }
        ],
        
        [LateralCoreFlexionState.LEFT]: [
          {
            targetState: LateralCoreFlexionState.NEUTRAL,
            condition: (metrics, stateData, timestamp) => 
              Math.abs(metrics.currentFlexionAngle) < NEUTRAL_THRESHOLD &&
              timestamp - stateData.lastStateChangeTime > MIN_TIME_IN_POSITION,
            action: (fsm, metrics, timestamp) => {
              fsm.stateData.lastStateChangeTime = timestamp;
              fsm.stateData.neutralReturnTime = timestamp;
              fsm.stateData.stableInNeutral = false;
              fsm.stateData.visitedStates.push('NEUTRAL');
            }
          }
        ]
      }
    };
    
    super(config);
    
    this.constructor.initialConfig = {
      initialState: config.initialState,
      initialStateData: { ...config.initialStateData },
      initialFormScores: { ...config.initialFormScores }
    };
  }
  
  process(results, timestamp) {
    const baseResult = super.process(results, timestamp);
    let isRepCompleted = false;
    
    if (this.stateData.inInternalCooldown && timestamp > this.stateData.cooldownEndTime) {
      this.stateData.inInternalCooldown = false;
    }
    
    const timeInNeutral = timestamp - this.stateData.neutralReturnTime;
    
    if (this.currentState === LateralCoreFlexionState.NEUTRAL && 
        !this.stateData.repCounted && 
        this.stateData.visitedStates.length > 1) {
      
      const lastVisitedState = this.stateData.visitedStates[this.stateData.visitedStates.length - 2];
    }
    
    if (this.currentState === LateralCoreFlexionState.NEUTRAL && 
        !this.stateData.stableInNeutral && 
        timeInNeutral > 250 && 
        !this.stateData.repCounted &&
        !this.stateData.inInternalCooldown &&
        timestamp - this.stateData.lastRepTime > MIN_TIME_BETWEEN_REPS) {
        
      let foundLeft = false;
      let foundRight = false;
      
      const recentStates = this.stateData.visitedStates.slice(-4);
      for (const state of recentStates) {
        if (state === 'LEFT') foundLeft = true;
        if (state === 'RIGHT') foundRight = true;
      }
      
      let countSide = null;
      if (foundRight && this.stateData.maxRightFlexionAngle > MIN_FLEXION_ANGLE) {
        countSide = 'right';
      } else if (foundLeft && Math.abs(this.stateData.maxLeftFlexionAngle) > MIN_FLEXION_ANGLE) {
        countSide = 'left';
      }
      
      if (countSide) {
        const maxAngle = countSide === 'right' 
          ? this.stateData.maxRightFlexionAngle 
          : Math.abs(this.stateData.maxLeftFlexionAngle);
        
        const optimalFlexionAngle = 20;
        const rangeOfMotionScore = Math.min(100, 100 - Math.abs(maxAngle - optimalFlexionAngle) * 3);
        const controlScore = Math.min(100, Math.max(0, 100 - (this.stateData.flexionVelocity > 20 ? (this.stateData.flexionVelocity - 20) * 2 : 0)));
        
        this.stateData.rangeOfMotionScore = rangeOfMotionScore;
        this.stateData.controlScore = controlScore;
        this.stateData.repFormScore = Math.round((rangeOfMotionScore * 0.7) + (controlScore * 0.3));
        this.formScores.rangeOfMotion = rangeOfMotionScore;
        this.formScores.control = controlScore;
        this.formScores.overall = this.stateData.repFormScore;
        
        this.repetitionCount++;
        isRepCompleted = true;
        
        // CRITICAL: Store per-rep data for session analytics
        // Use rangeOfMotionScore as the ROM for this specific rep (not rolling average)
        this.repHistory.push({
          rep: this.repetitionCount,
          formScore: this.stateData.repFormScore,
          rom: Math.round(rangeOfMotionScore), // ROM achieved in THIS rep
          timestamp: timestamp
        });
        
        if (DEBUG_LOGGING) {
          console.log(`FSM: Stored rep #${this.repetitionCount} - Form: ${this.stateData.repFormScore}, ROM: ${Math.round(rangeOfMotionScore)}`);
        }
        
        this.stateData.repCounted = true;
        this.stateData.lastRepTime = timestamp;
        this.stateData.lastSideCompleted = countSide;
        
        this.stateData.inInternalCooldown = true;
        this.stateData.cooldownEndTime = timestamp + INTERNAL_COOLDOWN;
        
        if (maxAngle < MIN_FLEXION_ANGLE + 5) {
          this.formFeedback = "Try to bend further for better ROM";
        } else if (maxAngle > MAX_FLEXION_ANGLE - 5) {
          this.formFeedback = "Reduce bend angle for safety";
        } else if (this.stateData.flexionVelocity > 25) {
          this.formFeedback = "Move more slowly for better control";
        } else if (!this.stateData.areBothHandsRaised && Math.random() < 0.3) {
          this.formFeedback = "Remember to keep hands raised";
        } else {
          this.formFeedback = "Good form!";
        }
      } else {
      }
    }
    
    if (this.currentState === LateralCoreFlexionState.NEUTRAL && 
        !this.stateData.stableInNeutral && 
        timeInNeutral > 500) {
      this.stateData.stableInNeutral = true;
    }
    
    return {
      isRepCompleted: isRepCompleted,
      formFeedback: this.formFeedback,
      exerciseState: this.getExerciseState()
    };
  }
  
  computeMetrics(landmarks, worldLandmarks, timestamp) {
    if (!landmarks || !landmarks[11] || !landmarks[12] || 
        !landmarks[23] || !landmarks[24]) {
      if (DEBUG_LOGGING) {
        console.log("LateralCoreFlexionFSM: Missing required landmarks");
      }
      return {};
    }
    
    const leftShoulder = landmarks[11];
    const rightShoulder = landmarks[12];
    const leftHip = landmarks[23];
    const rightHip = landmarks[24];
    
    const shoulderMidpoint = {
      x: (leftShoulder.x + rightShoulder.x) / 2,
      y: (leftShoulder.y + rightShoulder.y) / 2
    };
    
    const hipMidpoint = {
      x: (leftHip.x + rightHip.x) / 2,
      y: (leftHip.y + rightHip.y) / 2
    };
    
    const spineVectorX = shoulderMidpoint.x - hipMidpoint.x;
    const spineVectorY = shoulderMidpoint.y - hipMidpoint.y;
    const spineAngleRad = Math.atan2(spineVectorX, -spineVectorY);
    const spineAngleDeg = spineAngleRad * (180 / Math.PI);
    
    const currentAngle = spineAngleDeg;
    
    let areBothHandsRaised = false;
    if (landmarks[15] && landmarks[16]) {
      const leftWrist = landmarks[15];
      const rightWrist = landmarks[16];
      const isLeftHandRaised = leftWrist.y < leftShoulder.y;
      const isRightHandRaised = rightWrist.y < rightShoulder.y;
      areBothHandsRaised = isLeftHandRaised && isRightHandRaised;
    }
    this.stateData.areBothHandsRaised = areBothHandsRaised;
    
    if (this.stateData.currentFlexionAngle === undefined) {
      this.stateData.currentFlexionAngle = currentAngle;
    } else {
      const smoothingFactor = 0.3;
      this.stateData.currentFlexionAngle = (this.stateData.currentFlexionAngle * (1 - smoothingFactor)) + 
                                         (currentAngle * smoothingFactor);
    }
    
    // Debug logging to see current state and angle
    if (DEBUG_LOGGING && Math.round(timestamp) % 1000 < 50) { // Log every ~1 second
      console.log(`🔍 LateralCoreFlexion Debug:
        Current State: ${this.currentState}
        Flexion Angle: ${this.stateData.currentFlexionAngle.toFixed(1)}°
        Raw Angle: ${currentAngle.toFixed(1)}°
        Hands Raised: ${areBothHandsRaised}
        In Cooldown: ${this.stateData.inInternalCooldown}
        Thresholds: Neutral<${NEUTRAL_THRESHOLD}°, Flexion>${FLEXION_THRESHOLD}°`);
    }
    
    this.stateData.previousFlexionAngle = this.stateData.currentFlexionAngle;
    
    if (!this.stateData.flexionHistory) {
      this.stateData.flexionHistory = [];
    }
    
    if (this.stateData.flexionHistory.length > 10) {
      this.stateData.flexionHistory.shift();
    }
    this.stateData.flexionHistory.push({angle: this.stateData.currentFlexionAngle, time: timestamp});
    
    let flexionVelocity = 0;
    if (this.stateData.flexionHistory.length >= 2) {
      const first = this.stateData.flexionHistory[0];
      const last = this.stateData.flexionHistory[this.stateData.flexionHistory.length - 1];
      const timeDiff = (last.time - first.time) / 1000;
      if (timeDiff > 0) {
        const angleDiff = Math.abs(last.angle - first.angle);
        flexionVelocity = angleDiff / timeDiff;
      }
    }
    this.stateData.flexionVelocity = flexionVelocity;
    
    if (this.currentState === LateralCoreFlexionState.RIGHT && this.stateData.currentFlexionAngle > this.stateData.maxRightFlexionAngle) {
      this.stateData.maxRightFlexionAngle = this.stateData.currentFlexionAngle;
    } else if (this.currentState === LateralCoreFlexionState.LEFT && this.stateData.currentFlexionAngle < this.stateData.maxLeftFlexionAngle) {
      this.stateData.maxLeftFlexionAngle = this.stateData.currentFlexionAngle;
    }
    
    const isNeutral = Math.abs(this.stateData.currentFlexionAngle) < NEUTRAL_THRESHOLD;
    const isFlexedRight = this.stateData.currentFlexionAngle > FLEXION_THRESHOLD;
    const isFlexedLeft = this.stateData.currentFlexionAngle < -FLEXION_THRESHOLD;
    
    let flexionDirection = 'neutral';
    if (isFlexedRight) {
      flexionDirection = 'right';
    } else if (isFlexedLeft) {
      flexionDirection = 'left';
    }
    
    const rangeOfMotionScore = Math.min(100, Math.max(0, (Math.abs(this.stateData.currentFlexionAngle) / MIN_FLEXION_ANGLE) * 100));
    const controlScore = Math.min(100, Math.max(0, 100 - (flexionVelocity > 20 ? (flexionVelocity - 20) * 2 : 0)));
    
    let exerciseProgress = 0;
    if (this.stateData.currentFlexionAngle > 0) {
      exerciseProgress = Math.min(50, (this.stateData.currentFlexionAngle / MAX_FLEXION_ANGLE) * 50);
    } else {
      exerciseProgress = Math.max(-50, (this.stateData.currentFlexionAngle / MAX_FLEXION_ANGLE) * 50);
    }
    
    exerciseProgress = 50 + exerciseProgress;
    
    // Calculate rangeOfMotion as percentage (0-100) based on current angle vs target
    // Target is MAX_FLEXION_ANGLE (30°) for 100% ROM
    const rangeOfMotion = Math.min(100, (Math.abs(this.stateData.currentFlexionAngle) / MAX_FLEXION_ANGLE) * 100);
    
    return {
      currentFlexionAngle: this.stateData.currentFlexionAngle,
      previousFlexionAngle: this.stateData.previousFlexionAngle,
      flexionDirection,
      flexionVelocity,
      
      isNeutral,
      isFlexedRight,
      isFlexedLeft,
      
      rangeOfMotion,        // For base class updateFormScores()
      rangeOfMotionScore,   // Legacy compatibility
      controlScore,
      exerciseProgress,
      areBothHandsRaised
    };
  }
  
  updateFormScores(metrics, transitioned) {
    // Update ROM from metrics
    if (metrics.rangeOfMotion !== undefined) {
      this.formScores.rangeOfMotion = Math.round(metrics.rangeOfMotion);
    }
    
    // Update control score
    if (metrics.controlScore !== undefined) {
      this.formScores.control = Math.round(metrics.controlScore);
    }
    
    // Calculate overall form score: 60% ROM + 40% control
    this.formScores.overall = Math.round(
      (this.formScores.rangeOfMotion * 0.6) + 
      (this.formScores.control * 0.4)
    );
  }
  
  reset() {
    this.currentState = this.constructor.initialConfig.initialState;
    this.stateData = { ...this.constructor.initialConfig.initialStateData };
    this.formScores = { ...this.constructor.initialConfig.initialFormScores };
    this.repetitionCount = 0;
    this.lastTransitionTime = 0;
    this.formFeedback = "Stand upright, then bend sideways (right or left) and return to center. Keep your torso straight without leaning forward or backward.";
  }
  
  getRepetitionCount() {
    return this.repetitionCount;
  }
} 